import { useEffect, useRef } from 'react';
import { usePicks } from '../../context/PicksContext';
import { fetchProducts, fetchCatalogueProducts, fetchBraceCatalogueProducts } from '../../services/products.service';
import { Product } from '../../types';
import { decodePicks, resolveSharedPicks, getPicksParamFromUrl, clearPicksFromUrl } from '../../utils/picksShare';

/**
 * Silent loader that only runs when ?picks= is detected in the URL.
 * Loads products from the catalogue endpoints, resolves the shared picks,
 * and dispatches them to the PicksContext (which shows the banner/modal).
 */
export function SharedPicksResolver() {
  const { setSharedPicks, openSharedModal } = usePicks();
  const ranRef = useRef(false);

  useEffect(() => {
    if (ranRef.current) return;
    const encoded = getPicksParamFromUrl();
    if (!encoded) return;
    ranRef.current = true;

    const compactPicks = decodePicks(encoded);
    if (compactPicks.length === 0) {
      clearPicksFromUrl();
      return;
    }

    // Links from order emails carry &src=reorder — same mechanism, warmer copy
    const isReorder = new URLSearchParams(window.location.search).get('src') === 'reorder';

    // Load every product source we know about and unionise
    Promise.all([
      fetchProducts('compression').catch(() => [] as Product[]),
      fetchProducts('braces').catch(() => [] as Product[]),
      fetchCatalogueProducts().catch(() => [] as Product[]),
      fetchBraceCatalogueProducts().catch(() => [] as Product[]),
    ])
      .then((lists) => {
        const seen = new Set<string>();
        const merged: Product[] = [];
        for (const list of lists) {
          for (const p of list) {
            if (!seen.has(p.productId)) {
              seen.add(p.productId);
              merged.push(p);
            }
          }
        }
        const resolutions = resolveSharedPicks(compactPicks, merged);
        const validCount = resolutions.filter((r) => r.status === 'ok').length;
        if (resolutions.length > 0) {
          setSharedPicks(resolutions, isReorder);
          // Auto-open modal if at least one item is valid
          if (validCount > 0) {
            setTimeout(() => openSharedModal(), 500);
          }
        } else {
          clearPicksFromUrl();
        }
      })
      .catch(() => {
        clearPicksFromUrl();
      });
  }, [setSharedPicks, openSharedModal]);

  return null;
}
